import { useLayoutEffect, useRef, useEffect } from 'react';

export type UseResizeObserverCallback = (
    entry: ResizeObserverEntry,
    observer: ResizeObserver
) => any;

/**
 * 创建一个单例的 ResizeObserver 实例
 * 使用订阅模式来管理多个观察目标
 */
function createResizeObserver() {
    // SSR 环境检查
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
        return null;
    }

    let ticking = false;
    let allEntries: ResizeObserverEntry[] = [];

    const callbacks: Map<Element, Array<UseResizeObserverCallback>> = new Map();

    const observer = new ResizeObserver((entries: ResizeObserverEntry[], obs: ResizeObserver) => {
        allEntries = allEntries.concat(entries);
        if (!ticking) {
            window.requestAnimationFrame(() => {
                const triggered = new Set<Element>();
                for (let i = 0; i < allEntries.length; i++) {
                    if (triggered.has(allEntries[i].target)) continue;
                    triggered.add(allEntries[i].target);
                    const cbs = callbacks.get(allEntries[i].target);
                    cbs?.forEach((cb) => cb(allEntries[i], obs));
                }
                allEntries = [];
                ticking = false;
            });
        }
        ticking = true;
    });

    return {
        observer,
        subscribe(target: Element, callback: UseResizeObserverCallback) {
            observer.observe(target);
            const cbs = callbacks.get(target) ?? [];
            cbs.push(callback);
            callbacks.set(target, cbs);
        },
        unsubscribe(target: Element, callback: UseResizeObserverCallback) {
            const cbs = callbacks.get(target) ?? [];
            if (cbs.length === 1) {
                observer.unobserve(target);
                callbacks.delete(target);
                return;
            }
            const cbIndex = cbs.indexOf(callback);
            if (cbIndex !== -1) cbs.splice(cbIndex, 1);
            callbacks.set(target, cbs);
        },
    };
}

let _resizeObserver: ReturnType<typeof createResizeObserver>;

const getResizeObserver = () =>
    !_resizeObserver ? (_resizeObserver = createResizeObserver()) : _resizeObserver;

/**
 * 自定义的 useResizeObserver hook，解决 SSR 环境下 window 不存在的问题
 * 兼容 @react-hook/resize-observer 的 API
 * 
 * @param target - React ref 对象或 DOM 元素（可以为 null）
 * @param callback - 当元素大小改变时的回调函数，接收 (entry, observer) 两个参数
 * @returns ResizeObserver 实例（如果在浏览器环境）
 */
function useResizeObserver<T extends Element>(
    target: React.RefObject<T> | React.ForwardedRef<T> | T | null,
    callback: UseResizeObserverCallback
): ResizeObserver | undefined {
    const resizeObserver = getResizeObserver();
    const callbackRef = useRef<UseResizeObserverCallback>(callback);

    // 保持 callback 的引用最新
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // 使用 useLayoutEffect 来同步执行，避免闪烁
    useLayoutEffect(() => {
        // SSR 环境检查
        if (!resizeObserver) {
            return;
        }

        let didUnsubscribe = false;
        const targetEl = target && 'current' in target ? target.current : target;
        if (!targetEl) return;

        function cb(entry: ResizeObserverEntry, observer: ResizeObserver) {
            if (didUnsubscribe) return;
            callbackRef.current(entry, observer);
        }

        resizeObserver.subscribe(targetEl as Element, cb);

        return () => {
            didUnsubscribe = true;
            resizeObserver.unsubscribe(targetEl as Element, cb);
        };
    }, [target, resizeObserver]);

    return resizeObserver?.observer;
}

export default useResizeObserver;

